import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {TheBorder} from "../../TheBorder";
import {gsap, TimelineLite} from "gsap";
import {TheBorderSymbol} from "../../reels/TheBorderSymbol";
import {TheBorderXWaysSymbol} from "../../reels/TheBorderXWaysSymbol";
import {TheBorderSound} from "../../sound/TheBorderSound";
import {ISymbol} from "../../../core/reel/ReelInterfaces";
import {TheBorderGameMode} from "../../server/TheBorderGameMode";
import {TheBorderXNudgeView} from "../../view/TheBorderXNudgeView";
import {IClusterData, TheBorderUtils} from "../../utils/TheBorderUtils";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

/**
 * Created by Jerker Nord on 2021-10-28.
 */

export class TheBorderXWaysWinRunner implements SimplePresentation {

    private static DELAY_BETWEEN_EXPAND_NORMAL_SPIN:number = 0.2;
    private static DELAY_BETWEEN_EXPAND_FAST_SPIN:number = 0;

    private _mainTL:gsap.core.Timeline;

    private static canInfectInFS(xWaysPositionsInCluster:Set<string>, searchId:string):boolean {
        if(xWaysPositionsInCluster.has(searchId)) {
            return false;
        } else {
            const reelId:number = +searchId.split("_")[0],
                xWaysPositionsInClusterArr:string[] = Array.from(xWaysPositionsInCluster);

            for(let i:number = 0; i < xWaysPositionsInClusterArr.length; i++) {
                if(+(xWaysPositionsInClusterArr[i].split("_")[0]) === reelId) {
                    return true;
                }
            }

            return false;
        }
    }

    public shouldRun():boolean {
        return TheBorder.model.xWay;
    }

    public start(controller:SimplePresentationRunnerController):void {
        const newSymbolName:string = TheBorder.model.xWayTransformSymbol,
            isSize4:boolean = TheBorder.model.size4xWay;

        this._mainTL = gsap.timeline();
        let expandTLs:gsap.core.Timeline[] = [];
        let roomToExpand:boolean = true;
        let doHalfSymbolExpand:boolean = false;
        const fastSpin:boolean = (TheBorder.apiPlugin.settings.get(APISetting.FAST_SPIN, false));

        TheBorder.model.allXWaysRelatedPositions = [];

        for(let i:number = 0; i < TheBorder.model.xWayPositions.length; i++) {
            roomToExpand = true;
            doHalfSymbolExpand = false;
            let expandTl:gsap.core.Timeline = gsap.timeline({delay : fastSpin ? (TheBorderXWaysWinRunner.DELAY_BETWEEN_EXPAND_FAST_SPIN * i) : (TheBorderXWaysWinRunner.DELAY_BETWEEN_EXPAND_NORMAL_SPIN * i)}),
                isSplitRequired:boolean = true, // #222
                newSymbolPosition:number = TheBorder.model.xWayPositions[i].y,
                newXWaySymbolName:string = newSymbolName + "_H",
                isMidExpand:boolean = false,
                isFastAnimation:boolean = false,
                addedSymbol:TheBorderSymbol,
                xWaysSymbol:TheBorderXWaysSymbol = (TheBorder.allReelsView.getSymbolAtPosition(TheBorder.model.xWayPositions[i].x, TheBorder.model.xWayPositions[i].y) as TheBorderXWaysSymbol);

            if(xWaysSymbol.reelPosition === TheBorder.model.reels[TheBorder.model.xWayPositions[i].x].length) {
                //X-Ways is bottom symbol on reel. It should stay half size and just transfer itself.
                newSymbolPosition = TheBorder.model.xWayPositions[i].y - 1;

                if(TheBorder.model.xWayPositions.length > 1) {
                    expandTl.add([
                        xWaysSymbol.getExpandUpAnimation().restart(),
                        this.playExpandSound(),
                    ]);
                } else {
                    //Only one XWay, do fast version
                    isFastAnimation = true;
                    expandTl.add([
                        xWaysSymbol.getExpandAndRevealUpTL().restart(),
                        this.playExpandSound(),
                    ]);
                    expandTl.add(this.playRevealSound(), "-=0.42");
                }
            } else if(xWaysSymbol.reelPosition === 1) {
                //X-Ways is top symbol on reel. It should stay half size and just transfer itself.
                if(TheBorder.allReelsView.getReelLength(TheBorder.model.xWayPositions[i].x) !== 11) {
                    //There is room to expand
                    if(TheBorder.model.xWayPositions.length > 1) {
                        expandTl.add([
                            xWaysSymbol.getExpandDownAnimation().restart(),
                            this.playExpandSound()
                        ]);
                    } else {
                        //Only one XWay, do fast version
                        isFastAnimation = true;
                        expandTl.add([
                            xWaysSymbol.getExpandAndRevealDownTL().restart(),
                            this.playExpandSound()
                        ]);
                        expandTl.add(this.playRevealSound(), "-=0.42");
                    }
                } else {
                    //No room to expand, don't do expand animation
                    roomToExpand = false;
                    if(TheBorder.model.xWayPositions.length == 1) {
                        expandTl.add([
                            xWaysSymbol.getRevealNoRoomAnimation().restart(),
                            this.playRevealSound()
                        ]);
                    }
                }
            } else if(xWaysSymbol.reelPosition === 2 && TheBorder.allReelsView.getReelLength(TheBorder.model.xWayPositions[i].x) == 10) {
                newXWaySymbolName = newSymbolName;
                isMidExpand = true;
                //Half room to expand, do half symbol up expand animation
                doHalfSymbolExpand = true;
                if(TheBorder.model.xWayPositions.length > 1) {
                    expandTl.add([
                        xWaysSymbol.getExpandDownAnimation().restart(),
                        this.playExpandSound()
                    ]);
                } else {
                    //Only one XWay, do fast version
                    isFastAnimation = true;
                    expandTl.add([
                        xWaysSymbol.getExpandAndRevealDownTL().restart(),
                        this.playExpandSound()
                    ]);
                    expandTl.add(this.playRevealSound(), "-=0.42");
                }
            } else if(xWaysSymbol.reelPosition === 2 && (TheBorder.allReelsView.getSymbolAtPosition(xWaysSymbol.reelId, 0).sizeMultiplier == 0.5)) {
                newXWaySymbolName = newSymbolName;
                isMidExpand = true;
                //Half symbol at top position
                doHalfSymbolExpand = false;
                if(TheBorder.model.xWayPositions.length > 1) {
                    expandTl.add([
                        xWaysSymbol.getExpandDownAnimation().restart(),
                        this.playExpandSound()
                    ]);
                } else {
                    //Only one XWay, do fast version
                    isFastAnimation = true;
                    expandTl.add([
                        xWaysSymbol.getExpandAndRevealDownTL().restart(),
                        this.playExpandSound()
                    ]);
                    expandTl.add(this.playRevealSound(), "-=0.42");
                }
            } else {
                //There is room to expand
                newXWaySymbolName = newSymbolName;
                isMidExpand = true;
                if(TheBorder.model.xWayPositions.length > 1) {
                    expandTl.add([
                        xWaysSymbol.getExpandUpAndDownAnimation().restart(),
                        this.playExpandSound()
                    ]);
                } else {
                    //Only one XWay, do fast version
                    isFastAnimation = true;
                    expandTl.add([
                        xWaysSymbol.getExpandAndRevealUpAndDownTL().restart(),
                        this.playExpandSound()
                    ]);
                    expandTl.add(this.playRevealSound(), "-=0.42");
                }
            }

            if(!roomToExpand) {
                expandTl.add(() => {
                    const reelId:number = TheBorder.model.xWayPositions[i].x;
                    let splitLevel:number = 1;
                    if(isSize4) {
                        splitLevel = 2;
                    }

                    xWaysSymbol.splitLevel = splitLevel;
                    xWaysSymbol.symbolName = newSymbolName;
                    TheBorder.allReelsView.mainReelModels[reelId].reelView.splitSymbolIntoTwoHalf(xWaysSymbol);

                    TheBorder.allReelsView.mainReelModels[reelId].reelView.positionAllSymbolsOnReel(3 / 30);
                });
            } else if(doHalfSymbolExpand) {
                expandTl.add(() => {
                    const reelId:number = TheBorder.model.xWayPositions[i].x;
                    let splitLevel:number = 1;
                    if(isSize4) {
                        splitLevel = 2;
                    }

                    xWaysSymbol.splitLevel = 1;
                    xWaysSymbol.symbolName = newSymbolName;
                    xWaysSymbol.setCurrentStateSpriteTexture();
                    xWaysSymbol.setNewStateSpriteTexture();

                    TheBorder.allReelsView.mainReelModels[reelId].reelView.makeTopSymbolHalf();

                    addedSymbol = (TheBorder.allReelsView.mainReelModels[reelId].reelView.addNewSymbolToReel(newSymbolPosition, newSymbolName, 0.5, 1, true) as TheBorderSymbol);
                    addedSymbol.symbolName += "_H";
                    addedSymbol.sizeMultiplier = 0.5;
                    addedSymbol.splitLevel = splitLevel;
                    addedSymbol.setCurrentStateSpriteTexture();
                    addedSymbol.setNewStateSpriteTexture();

                    if(isSize4) {
                        TheBorder.allReelsView.mainReelModels[reelId].reelView.splitSymbolIntoTwoHalf(xWaysSymbol);
                    }

                    TheBorder.allReelsView.mainReelModels[reelId].reelView.positionAllSymbolsOnReel(3 / 30);
                }, isFastAnimation ? "-=0.5" : "-=0.7");
                expandTl.add(() => {addedSymbol.show()}, "-=0.4");
            } else {
                expandTl.add(() => {
                    const reelId:number = TheBorder.model.xWayPositions[i].x;
                    let splitLevel:number = 1;

                    if(isSize4 && ((xWaysSymbol.reelPosition === 1) || (xWaysSymbol.reelPosition === TheBorder.model.reels[reelId].length))) {
                        splitLevel = 2;
                    }

                    TheBorder.allReelsView.mainReelModels[reelId].reelView.makeTopAndBottomSymbolHalf();

                    // #222, following check means, xWays symbol is the top symbol that transformed into half symbol. Very rare case.
                    if(isMidExpand && xWaysSymbol.symbolName === "X_WAYS_H") {
                        newXWaySymbolName += "_H";
                        if(isSize4) {
                            splitLevel = 2;
                        }
                        isSplitRequired = false;
                    }

                    xWaysSymbol.getSplitAndChangeAnimation(splitLevel, newXWaySymbolName);

                    addedSymbol = (TheBorder.allReelsView.mainReelModels[reelId].reelView.addNewSymbolToReel(newSymbolPosition, newSymbolName, 1, 1, true) as TheBorderSymbol);

                    TheBorder.allReelsView.mainReelModels[reelId].reelView.positionAllSymbolsOnReel(3 / 30);
                }, isFastAnimation ? "-=0.5" : "-=0.7");

                if(isSize4) {
                    expandTl.add(() => {
                        TheBorder.allReelsView.mainReelModels[TheBorder.model.xWayPositions[i].x].reelView.splitSymbolIntoTwoHalf(addedSymbol);
                    }, isFastAnimation ? "-=0.2" : "+=0");
                    if(isMidExpand) {
                        if(isFastAnimation) {
                            expandTl.add(() => {
                                // this flag updates inside the timeline, so keep it like this.
                                if(isSplitRequired) {
                                    TheBorder.allReelsView.mainReelModels[TheBorder.model.xWayPositions[i].x].reelView.splitSymbolIntoTwoHalf(xWaysSymbol, isMidExpand);
                                }
                            }, "-=0.2");
                        } else {
                            expandTl.add(() => {
                                // this flag updates inside the timeline, so keep it like this.
                                if(isSplitRequired) {
                                    TheBorder.allReelsView.mainReelModels[TheBorder.model.xWayPositions[i].x].reelView.splitSymbolIntoTwoHalf(xWaysSymbol, isMidExpand);
                                }
                            });
                        }
                    }
                }
                expandTl.add(() => {addedSymbol.show()}, "-=0.4");
            }

            expandTLs.push(expandTl);
        }

        this._mainTL.add(expandTLs);

        if(TheBorder.model.xWayPositions.length > 1) {
            let revealTLs:gsap.core.Timeline[] = [];

            for(let i:number = 0; i < TheBorder.model.xWayPositions.length; i++) {
                let revealTL:gsap.core.Timeline = gsap.timeline();

                const xWaysSymbol:TheBorderXWaysSymbol = (TheBorder.allReelsView.getSymbolAtPosition(TheBorder.model.xWayPositions[i].x, TheBorder.model.xWayPositions[i].y) as TheBorderXWaysSymbol);

                if(xWaysSymbol.reelPosition === TheBorder.model.reels[TheBorder.model.xWayPositions[i].x].length) {
                    //X-Ways is bottom symbol on reel.
                    revealTL.add(xWaysSymbol.getRevealDownAnimation().restart());
                } else if(xWaysSymbol.reelPosition == 1) {
                    //X-Ways is top symbol on reel.
                    if(TheBorder.allReelsView.getReelLength(TheBorder.model.xWayPositions[i].x) !== 11) {
                        revealTL.add(xWaysSymbol.getRevealUpAnimation().restart());
                    } else {
                        //No room
                        if(TheBorder.model.xWayPositions.length > 1) {
                            revealTL.add(xWaysSymbol.getRevealNoRoomAnimation().restart());
                        }
                    }
                } else if(xWaysSymbol.reelPosition == 2 && TheBorder.allReelsView.getReelLength(TheBorder.model.xWayPositions[i].x) == 10) {
                    //Half room
                    revealTL.add(xWaysSymbol.getRevealUpAnimation().restart());
                } else {
                    revealTL.add(xWaysSymbol.getRevealUpAndDownAnimation().restart());
                }
                revealTL.add(() => {
                    xWaysSymbol.turnOffSymbolSpine();
                });
                revealTLs.push(revealTL);
            }
            TheBorder.model.xWayPositions.length && revealTLs.push(gsap.timeline().add(this.playRevealSound()));

            this._mainTL.add(revealTLs, "-=0.3");
        }

        this._mainTL.add(() => {
            if(TheBorder.model.gameData.mode.indexOf(TheBorderGameMode.FREESPIN) > -1) {
                this.doInfection(controller, isSize4, newSymbolName);
            } else {
                this.doFinish(controller);
            }
        });
    }

    private doFinish(controller:SimplePresentationRunnerController):void {
        TheBorder.xWaysSymbols.turnOffAllXWaysSymbols();
        controller.finish();
    }

    private doInfection(controller:SimplePresentationRunnerController, isSize4:boolean, newSymbolName:string):void {
        const tl:TimelineLite = new TimelineLite({
                onComplete : () => {
                    this.doFinish(controller);
                }
            }),
            reels:string[][] = TheBorderXNudgeView.getReelsData(),
            isSFS:boolean = (TheBorder.model.gameData.mode.indexOf(TheBorderGameMode.SUPER_FREESPIN) > -1),
            allXWaysRelatedPositions:string[] = [];

        for(let i:number = 0; i < TheBorder.model.xWayPositions.length; i++) {
            const reelId:number = TheBorder.model.xWayPositions[i].x,
                rowId:number = TheBorder.model.xWayPositions[i].y;
            let spliceCount:number = 2;
            if(isSize4) {
                spliceCount = 4;
                if((rowId === 0) || (rowId === 1 && TheBorder.model.reels[reelId][0].indexOf("_H") > -1) || (rowId === TheBorder.model.reels[reelId].length - 1)) {
                    spliceCount = 3;
                }
            }
            for(let j:number = 0; j < spliceCount; j++) {
                allXWaysRelatedPositions.push(reelId + "_" + (rowId + j));
            }
        }
        TheBorder.model.allXWaysRelatedPositions = allXWaysRelatedPositions;

        const checkedSymArr:string[] = [],
            clusters:IClusterData[] = TheBorderUtils.getClustersForSymbol(reels, newSymbolName, checkedSymArr);
        let cluster:IClusterData;
        let revealSoundPlayed:boolean = false;

        for(let i:number = 0; i < clusters.length; i++) {
            cluster = clusters[i];

            if(cluster.xWaysPositionsInCluster.size > 0) {
                cluster.info.forEach((val:string) => {
                    const symPos:string[] = val.split("_"),
                        reelId:number = +symPos[0];
                    let sym:ISymbol = TheBorder.allReelsView.getSymbolAtPosition(reelId, +symPos[1]),
                        canInfect:boolean;

                    if(isSFS) {
                        canInfect = (allXWaysRelatedPositions.indexOf(val) === -1);
                    } else {
                        canInfect = TheBorderXWaysWinRunner.canInfectInFS(cluster.xWaysPositionsInCluster, val);
                    }

                    if(canInfect && sym && !TheBorderUtils.isWild(sym.symbolName) && !sym.hasInfected) {
                        sym.hasInfected = true;
                        if(!revealSoundPlayed) {
                            revealSoundPlayed = true;
                            tl.add(this.playRevealSound(), 0.15);
                        }
                        tl.add(TheBorder.xWaysView.playAnimation((sym as TheBorderSymbol)), 0);
                        tl.add(() => {
                            if(sym.sizeMultiplier === 0.5) {
                                sym.splitLevel *= (isSize4 ? 4 : 2);
                            } else {
                                TheBorder.allReelsView.mainReelModels[reelId].reelView.splitSymbolIntoTwoHalf(sym);
                                if(isSize4) {
                                    sym.splitLevel *= 2;
                                    TheBorder.allReelsView.getSymbolAtPosition(reelId, sym.reelPosition)!.splitLevel *= 2;
                                }
                            }
                        });
                    }
                });
            }
        }

        tl.to(this, {
            duration : 0.2
        });
    }

    private playExpandSound():() => void {
        return () => TheBorder.soundPlayer.playSound(TheBorderSound.WAYS_SOUNDS[Math.floor(Math.random() * TheBorderSound.WAYS_SOUNDS.length)]); //#50
    }

    private playRevealSound():() => void {
        return () => TheBorder.soundPlayer.playSound(TheBorderSound.WAYS_REVEAL); //#50
    }
}